import { Check, PlusCircle, Sparkles, Trash } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { CitationViewer } from '@/components/citations/CitationViewer';
import { EvidenceData } from '@/components/citations/EvidenceCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ReadArtifactExtraction,
  useSuggestionsExtractionsQuery,
} from '@/services/api/generated/suggestionsService';
import {
  BodyPart,
  ReadInjuryWithSuggestion,
  ReadSuggestionSummary,
  useInjuryAcceptMutation,
  useInjuryCreateMutation,
  useInjuryDestroyMutation,
  useInjuryListWithSuggestionsQuery,
  useInjuryUpdateMutation,
} from '@/services/api/injuriesService';

const BODY_PART_LABELS: Record<BodyPart, string> = {
  head: 'Head',
  face: 'Face',
  cervical_spine: 'Cervical Spine',
  shoulder_left: 'Shoulder (Left)',
  shoulder_right: 'Shoulder (Right)',
  arm_left: 'Arm (Left)',
  arm_right: 'Arm (Right)',
  elbow_left: 'Elbow (Left)',
  elbow_right: 'Elbow (Right)',
  wrist_left: 'Wrist (Left)',
  wrist_right: 'Wrist (Right)',
  hand_left: 'Hand (Left)',
  hand_right: 'Hand (Right)',
  chest: 'Chest',
  thoracic_spine: 'Thoracic Spine',
  lumbar_spine: 'Lumbar Spine',
  abdomen: 'Abdomen',
  hip_left: 'Hip (Left)',
  hip_right: 'Hip (Right)',
  leg_left: 'Leg (Left)',
  leg_right: 'Leg (Right)',
  knee_left: 'Knee (Left)',
  knee_right: 'Knee (Right)',
  ankle_left: 'Ankle (Left)',
  ankle_right: 'Ankle (Right)',
  foot_left: 'Foot (Left)',
  foot_right: 'Foot (Right)',
  other: 'Other',
};

const BODY_PARTS = Object.keys(BODY_PART_LABELS) as BodyPart[];

/**
 * Convert extraction data to EvidenceData format for CitationViewer
 */
function extractionsToEvidence(
  extractions: ReadArtifactExtraction[]
): EvidenceData[] {
  return extractions.map((extraction) => ({
    artifact_id: extraction.artifact_id,
    citation_ids: extraction.citations
      ?.map((c) => c.citation_id)
      .filter((id): id is string => Boolean(id)),
    excerpts: extraction.citations
      ?.map((c) => c.excerpt)
      .filter((e): e is string => Boolean(e)),
    confidence: extraction.extra?.confidence as number | undefined,
    rationale: extraction.extra?.reasoning as string | undefined,
    context: extraction.extra?.context as string | undefined,
    created_at: extraction.created_at,
  }));
}

interface InjuryCitationViewerProps {
  suggestion: ReadSuggestionSummary;
  matterId: string;
  bodyPartLabel: string;
}

/**
 * Lazy-loading citation viewer that fetches extractions when rendered
 */
const InjuryCitationViewer: React.FC<InjuryCitationViewerProps> = ({
  suggestion,
  matterId,
  bodyPartLabel,
}) => {
  const {
    data: extractionsData,
    isLoading,
    error,
  } = useSuggestionsExtractionsQuery({
    matterId,
    id: suggestion.id,
  });

  // Handle both array and single object responses (API type mismatch)
  const extractions = extractionsData
    ? Array.isArray(extractionsData)
      ? extractionsData
      : [extractionsData]
    : [];
  const evidence = extractionsToEvidence(extractions);

  // Don't render if no extractions and not loading
  if (!isLoading && evidence.length === 0) {
    return null;
  }

  return (
    <CitationViewer
      evidence={evidence}
      matterId={matterId}
      dialogTitle={`Sources for ${bodyPartLabel} Injury`}
      compactTrigger
      isLoading={isLoading}
      error={Boolean(error)}
      buttonVariant="ghost"
      buttonClassName="h-5 px-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
    />
  );
};

interface InjuryRowProps {
  injury: ReadInjuryWithSuggestion;
  personId: string;
  matterId?: string;
  isEditing: boolean;
}

const InjuryRow: React.FC<InjuryRowProps> = ({
  injury,
  personId,
  matterId,
  isEditing,
}) => {
  const [updateInjury, { isLoading: isUpdating }] = useInjuryUpdateMutation();
  const [deleteInjury, { isLoading: isDeleting }] = useInjuryDestroyMutation();
  const [acceptInjury, { isLoading: isAccepting }] = useInjuryAcceptMutation();
  const [localDescription, setLocalDescription] = useState(
    injury.description || ''
  );

  const isSuggested = injury.approval_status === 'suggested';
  const hasSuggestion = Boolean(injury.pending_suggestion);

  const handleDescriptionBlur = async () => {
    if (localDescription !== (injury.description || '')) {
      try {
        await updateInjury({
          personId,
          id: injury.id,
          pk: injury.id,
          updateInjury: {
            id: injury.id,
            description: localDescription || null,
          },
        }).unwrap();
      } catch {
        toast.error('Failed to update injury');
        setLocalDescription(injury.description || '');
      }
    }
  };

  const handleDelete = async () => {
    try {
      await deleteInjury({
        personId,
        id: injury.id,
        pk: injury.id,
      }).unwrap();
      toast.success('Injury removed');
    } catch {
      toast.error('Failed to remove injury');
    }
  };

  const handleAccept = async () => {
    try {
      await acceptInjury({
        personId,
        id: injury.id,
        pk: injury.id,
      }).unwrap();
      toast.success('Injury verified');
    } catch {
      toast.error('Failed to verify injury');
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 w-40 flex items-center gap-1">
          <span className="text-xs font-medium text-gray-700">
            {BODY_PART_LABELS[injury.body_part]}
          </span>
          {isSuggested && (
            <Tooltip>
              <TooltipTrigger>
                <Badge
                  variant="outline"
                  className="h-4 px-1 text-[9px] bg-amber-50 text-amber-700 border-amber-200"
                >
                  <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                  AI
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Auto-extracted, not yet verified</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex-1">
          <Input
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            onBlur={handleDescriptionBlur}
            placeholder="Describe the injury..."
            disabled={isUpdating}
            className="h-7 text-xs border-0 shadow-none focus-visible:ring-0 px-0"
          />
        </div>
        {isSuggested && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAccept}
                disabled={isAccepting}
                className="h-6 px-1.5 text-green-600/70 hover:text-green-700 hover:bg-green-50"
              >
                <Check className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Verify this injury</p>
            </TooltipContent>
          </Tooltip>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          disabled={isDeleting}
          className="h-6 px-1.5 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
        >
          <Trash className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 text-xs group">
      <span className="font-medium text-gray-700 w-40 flex-shrink-0 flex items-center gap-1">
        {BODY_PART_LABELS[injury.body_part]}
        {isSuggested && (
          <Tooltip>
            <TooltipTrigger>
              <Badge
                variant="outline"
                className="h-4 px-1 text-[9px] bg-amber-50 text-amber-700 border-amber-200"
              >
                <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                AI
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Auto-extracted, not yet verified</p>
            </TooltipContent>
          </Tooltip>
        )}
      </span>
      <span className="text-gray-600 flex-1">{injury.description || '—'}</span>
      {/* Citation viewer - shows sources when suggestion has extractions */}
      {hasSuggestion && matterId && (
        <InjuryCitationViewer
          suggestion={injury.pending_suggestion!}
          matterId={matterId}
          bodyPartLabel={BODY_PART_LABELS[injury.body_part]}
        />
      )}
      {isSuggested && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAccept}
              disabled={isAccepting}
              className="h-5 px-1.5 text-green-600/70 hover:text-green-700 hover:bg-green-50 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Check className="h-3 w-3 mr-0.5" />
              <span className="text-[10px]">Verify</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Mark as verified by human</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

interface AddInjuryRowProps {
  personId: string;
  existingBodyParts: Set<BodyPart>;
  onCancel: () => void;
}

const AddInjuryRow: React.FC<AddInjuryRowProps> = ({
  personId,
  existingBodyParts,
  onCancel,
}) => {
  const [createInjury, { isLoading }] = useInjuryCreateMutation();
  const [selectedBodyPart, setSelectedBodyPart] = useState<BodyPart | ''>('');
  const [description, setDescription] = useState('');

  const availableBodyParts = BODY_PARTS.filter(
    (bp) => !existingBodyParts.has(bp)
  );

  const handleSave = async () => {
    if (!selectedBodyPart) {
      toast.error('Please select a body part');
      return;
    }

    try {
      await createInjury({
        personId,
        createInjury: {
          person_id: personId,
          body_part: selectedBodyPart,
          description: description || null,
        },
      }).unwrap();
      toast.success('Injury added');
      onCancel();
    } catch {
      toast.error('Failed to add injury');
    }
  };

  return (
    <div className="flex items-start gap-2 bg-gray-50 p-2 rounded-md">
      <div className="w-40 flex-shrink-0">
        <Select
          value={selectedBodyPart}
          onValueChange={(v) => setSelectedBodyPart(v as BodyPart)}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder="Body part..." />
          </SelectTrigger>
          <SelectContent>
            {availableBodyParts.map((bp) => (
              <SelectItem key={bp} value={bp} className="text-xs">
                {BODY_PART_LABELS[bp]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1">
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the injury..."
          className="h-7 text-xs"
        />
      </div>
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="h-7 px-2 text-xs"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isLoading || !selectedBodyPart}
          className="h-7 px-2 text-xs"
        >
          Add
        </Button>
      </div>
    </div>
  );
};

interface InjuriesFieldsProps {
  personId: string | undefined;
  matterId?: string;
  isEditing: boolean;
}

export const InjuriesFields: React.FC<InjuriesFieldsProps> = ({
  personId,
  matterId,
  isEditing,
}) => {
  const [isAdding, setIsAdding] = useState(false);

  const { data: injuries = [], isLoading } = useInjuryListWithSuggestionsQuery(
    { personId: personId! },
    { skip: !personId }
  );

  // Handle both array and single object responses
  const injuriesList = Array.isArray(injuries) ? injuries : [injuries];

  const existingBodyParts = new Set(injuriesList.map((i) => i.body_part));

  if (!personId) {
    return (
      <div className="border-t border-gray-100">
        <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-3">
          <dt className="text-xs font-medium text-gray-900">Injuries</dt>
          <dd className="mt-0.5 text-xs sm:col-span-2 sm:mt-0">
            <span className="text-gray-400 italic">
              Link to a person to track injuries
            </span>
          </dd>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-100">
      <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-3">
        <dt className="text-xs font-medium text-gray-900">
          <div className="flex justify-between items-center">
            <span>Injuries</span>
            {isEditing && !isAdding && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdding(true)}
                className="h-6 text-[10px] text-muted-foreground/60 hover:text-primary/80 px-1.5"
              >
                <PlusCircle className="mr-0.5 h-3 w-3" />
                <span className="hidden sm:inline">Add Injury</span>
              </Button>
            )}
          </div>
        </dt>
        <dd className="mt-0.5 text-xs sm:col-span-2 sm:mt-0">
          {isLoading ? (
            <span className="text-gray-400 italic">Loading...</span>
          ) : injuriesList.length === 0 && !isAdding ? (
            <span className="text-gray-400 italic">No injuries recorded</span>
          ) : (
            <div className="space-y-2">
              {injuriesList.map((injury) => (
                <InjuryRow
                  key={injury.id}
                  injury={injury}
                  personId={personId}
                  matterId={matterId}
                  isEditing={isEditing}
                />
              ))}
              {isAdding && (
                <AddInjuryRow
                  personId={personId}
                  existingBodyParts={existingBodyParts}
                  onCancel={() => setIsAdding(false)}
                />
              )}
            </div>
          )}
        </dd>
      </div>
    </div>
  );
};
